/**
 * mmc_nav_categories.js
 * Navigation component with category grouping and search
 */

import React, { useState, useMemo, useEffect } from 'react';
import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react';
import { get_icon, get_category_icon } from '../utils/icon_map';
import { MmcInput, MmcLabel, MmcBadge } from './ui';

const MmcNavCategories = ({ games = [], onGameSelect = () => { }, activeTab = null }) => {
  const [search_query, set_search_query] = useState('');
  const [expanded_categories, set_expanded_categories] = useState({});

  const grouped_modules = useMemo(() => {
    const grouped = {};
    const category_display_names = {};

    // 1. Map modules and aggregate category metadata
    games
      .filter(m => m.enabled !== false)
      .forEach(module => {
        // Handle both nested metadata and flat manifest (for games)
        const raw_category = module.metadata?.category || module.category || 'General';
        const category_key = raw_category.toLowerCase();

        const category_icon = module.metadata?.category_icon || module.category_icon;
        const category_order = module.metadata?.category_order || module.category_order;

        if (!grouped[category_key]) {
          category_display_names[category_key] = raw_category;
          grouped[category_key] = {
            items: [],
            icon: category_icon,
            order: category_order ?? 999
          };
        } else {
          if (!grouped[category_key].icon && category_icon) {
            grouped[category_key].icon = category_icon;
          }
          if (category_order !== undefined && category_order < grouped[category_key].order) {
            grouped[category_key].order = category_order;
          }
        }

        grouped[category_key].items.push(module);
      });

    // 2. Sort modules within categories
    Object.values(grouped).forEach(group => {
      group.items.sort((a, b) => (a.order || 999) - (b.order || 999));
    });

    // 3. Sort entries and reconstruct result with display names
    const result = {};
    Object.keys(grouped)
      .sort((a, b) => {
        if (grouped[a].order !== grouped[b].order) return grouped[a].order - grouped[b].order;
        return category_display_names[a].localeCompare(category_display_names[b]);
      })
      .forEach(key => {
        result[category_display_names[key]] = grouped[key];
      });

    return result;
  }, [games]);

  const filtered_modules = useMemo(() => {
    if (!search_query.trim()) return grouped_modules;
    const query = search_query.toLowerCase();
    const filtered = {};
    Object.entries(grouped_modules).forEach(([category, group]) => {
      const filtered_items = group.items.filter(
        m => m.display_name.toLowerCase().includes(query) || category.toLowerCase().includes(query)
      );
      if (filtered_items.length > 0) filtered[category] = { ...group, items: filtered_items };
    });
    return filtered;
  }, [grouped_modules, search_query]);

  useEffect(() => {
    if (search_query.trim()) {
      const all_expanded = {};
      Object.keys(filtered_modules).forEach(cat => all_expanded[cat] = true);
      set_expanded_categories(all_expanded);
    }
  }, [search_query, filtered_modules]);

  // Expand category of active tab
  useEffect(() => {
    if (activeTab) {
      const activeModule = games.find(m => m.id === activeTab);
      const category = activeModule?.metadata?.category || activeModule?.category || 'General';
      const display_version = Object.keys(grouped_modules).find(k => k.toLowerCase() === category.toLowerCase());
      if (display_version) {
        set_expanded_categories(prev => ({ ...prev, [display_version]: true }));
      }
    }
  }, [activeTab, games, grouped_modules]);

  return (
    <div className="mmc-nav-categories">
      <div className="mmc-nav-search mmc-stack">
        <MmcInput
          placeholder="SEARCH GAMES..."
          icon={MagnifyingGlass}
          value={search_query}
          onChange={(e) => set_search_query(e.target.value)}
          aria-label="Search games"
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', padding: '0 4px' }}>
          <MmcLabel style={{ fontSize: '0.65rem', opacity: 0.4 }}>Quick search</MmcLabel>
          <kbd style={{ fontSize: '0.6rem' }} aria-hidden="true">⌘ K</kbd>
        </div>
      </div>

      <div className="mmc-categories-list" role="list">
        {Object.entries(filtered_modules).map(([category, group]) => (
          <div key={category} className={`mmc-category-group ${expanded_categories[category] ? 'open' : ''}`} role="listitem">
            <button
              className="mmc-category-header"
              onClick={() => set_expanded_categories(prev => ({ ...prev, [category]: !prev[category] }))}
              aria-expanded={!!expanded_categories[category]}
            >
              <div className="mmc-category-header-left">
                <span className="mmc-category-header-icon" aria-hidden="true">
                  {get_category_icon(category, group.icon)}
                </span>
                <span>{category}</span>
              </div>
              <CaretDown size={14} weight="bold" className="mmc-category-icon" aria-hidden="true" />
            </button>

            {expanded_categories[category] && (
              <ul className="mmc-category-items">
                {group.items.map(module => (
                  <li key={module.id}>
                    <button
                      className={`mmc-category-item-button ${activeTab === module.id ? 'active' : ''}`}
                      onClick={() => onGameSelect(module.id)}
                      aria-current={activeTab === module.id ? 'page' : undefined}
                    >
                      <span className="mmc-item-icon" aria-hidden="true">
                        {get_icon(module.icon || 'cube', 18, "bold")}
                      </span>
                      <span className="mmc-item-text">{module.display_name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MmcNavCategories;
